import { collection, query, where, getDocs, getDoc, doc, getFirestore, Timestamp, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFirebaseApp } from '../firebaseApp';
import { OWNER_ROLES, MANAGER_ROLES } from '../../constants/roles';
import type { BookingItem } from '../../types/bookings';
import type { ServiceType } from '../../types/services';
import { serviceColorMap } from '../../types/services';

let _db: ReturnType<typeof getFirestore> | undefined;
function getDb() {
  if (!_db) {
    _db = getFirestore(getFirebaseApp(), 'rsmaidsbooking');
    console.log('[bookingsCache] Firestore bookings DB initialized');
  }
  return _db;
}

// A booking's amount (price) is kept in `bookingAmounts/{bookingId}`, which only owners and managers can read, so a staff or lead
// account never receives it: for them a booking has no price. These helpers add the amounts for the roles that may read them.
async function canReadAmounts(): Promise<boolean> {
  const user = getAuth(getFirebaseApp()).currentUser;
  if (!user) return false;
  try {
    const role = (await user.getIdTokenResult()).claims.role;
    return ([...OWNER_ROLES, ...MANAGER_ROLES] as string[]).includes(String(role));
  } catch {
    return false;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function priceFromAmounts(d: Record<string, any>): number | undefined {
  return d.price?.amount != null ? parseFloat(d.price.amount) : undefined;
}

function withAmounts(items: BookingItem[], amounts: Map<string, number | undefined>): BookingItem[] {
  if (amounts.size === 0) return items;
  return items.map((b) => (amounts.has(b.id) ? { ...b, price: amounts.get(b.id) } : b));
}

async function loadAmounts(constraints: ReturnType<typeof where>[]): Promise<Map<string, number | undefined>> {
  const amounts = new Map<string, number | undefined>();
  if (!(await canReadAmounts())) return amounts;
  try {
    const snap = await getDocs(query(collection(getDb(), 'bookingAmounts'), ...constraints));
    snap.docs.forEach((d) => amounts.set(d.id, priceFromAmounts(d.data())));
  } catch (err) {
    console.warn('[bookingsCache] bookingAmounts failed:', err);
  }
  return amounts;
}

const SERVICE_TYPE_KEYS = Object.keys(serviceColorMap) as ServiceType[];

function mapStatus(s: string): BookingItem['status'] {
  switch ((s || '').toUpperCase()) {
    case 'RESCHEDULED':
    case 'BOOKED':
    case 'CONFIRMED': return 'confirmed';
    case 'COMPLETED': return 'completed';
    case 'CANCELED':
    case 'CANCELLED': return 'cancelled';
    case 'PAYMENT_AWAITING': return 'due';
    default: return 'pending';
  }
}

const normalizeServiceName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

function mapServiceType(name: string): ServiceType {
  if (!name) return 'Other';
  const target = normalizeServiceName(name);
  const match = SERVICE_TYPE_KEYS.find(k => {
    const key = normalizeServiceName(k);
    return target.includes(key) || key.includes(target);
  });
  return match || 'Other';
}

function formatLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapDoc(id: string, d: Record<string, any>): BookingItem {
  const startDate: Date = (d.startDate as Timestamp)?.toDate?.() ?? new Date();
  const endDate: Date = (d.endDate as Timestamp)?.toDate?.() ?? new Date();
  const localDate = formatLocalDate(startDate);

  return {
    id,
    title: d.serviceName || d.customerName || 'Booking',
    startTime: startDate,
    endTime: endDate,
    date: localDate,
    serviceType: mapServiceType(d.serviceName || ''),
    status: mapStatus(d.status || ''),
    notes: d.notes,
    location: d.location?.address || d.location?.name,
    contactId: d.contactId || '',
    contact: {
      id: d.contactId || '',
      name: d.customerName || '',
      email: d.email || '',
      phone: d.phone || '',
      address: d.location?.address || '',
      avatarUrl: d.avatarUrl || undefined,
    },
    staff: d.staffName || undefined,
    resource: d.resourceName || undefined,
    price: d.price?.amount != null ? parseFloat(d.price.amount) : undefined,
    zohoPaid: d.zohoPaid != null ? Boolean(d.zohoPaid) : undefined,
    zohoPaymentStatus: d.zohoPaymentStatus || undefined,
  };
}

function mapBlockedSlotDoc(id: string, d: Record<string, unknown>): BookingItem {
  const startDate = (d.startDate as Timestamp | null | undefined)?.toDate?.() ?? new Date();
  const fallbackEnd = new Date(startDate.getTime() + 30 * 60 * 1000);
  const rawEnd = (d.endDate as Timestamp | null | undefined)?.toDate?.();
  const endDate = rawEnd && rawEnd.getTime() > startDate.getTime() ? rawEnd : fallbackEnd;
  const title = typeof d.title === 'string' && d.title.trim() ? d.title.trim() : 'Blocked Time';
  const staffName = typeof d.staffName === 'string' ? d.staffName : '';
  const scheduleId = typeof d.scheduleId === 'string' ? d.scheduleId : '';
  const notes = typeof d.notes === 'string' ? d.notes : '';
  const transparency = typeof d.transparency === 'string' ? d.transparency : 'OPAQUE';
  const revision = typeof d.revision === 'string' ? d.revision : '';
  const recurrenceType = typeof d.recurrenceType === 'string' ? d.recurrenceType : '';
  const recurringEventId = typeof d.recurringEventId === 'string' ? d.recurringEventId : '';
  const status = typeof d.status === 'string' ? mapStatus(d.status) : 'confirmed';

  return {
    id: `blocked-${id}`,
    title: `${title} (${startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - ${endDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })})`,
    type: 'blocked',
    startTime: startDate,
    endTime: endDate,
    date: formatLocalDate(startDate),
    serviceType: 'Other',
    status,
    notes,
    location: '',
    contactId: '',
    contact: {
      id: '',
      name: staffName,
      email: '',
      phone: '',
      address: '',
    },
    staff: staffName || undefined,
    resource: scheduleId || undefined,
    transparency,
    duration: Math.round((endDate.getTime() - startDate.getTime()) / 60000),
    createdAt: startDate,
    updatedAt: (d.updatedAt as Timestamp | null | undefined)?.toDate?.() ?? endDate,
    agendaItem: {
      _id: id,
      type: 'BLOCKED_TIME',
      title,
      notes,
      revision: revision || undefined,
      recurrenceType: recurrenceType || undefined,
      recurringEventId: recurringEventId || undefined,
      scheduleId: scheduleId || undefined,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      transparency,
      isBlocked: true,
      isAppointment: false,
      staffMember: staffName ? { _id: scheduleId || id, name: staffName } : null,
      slot: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        staffMember: staffName ? { _id: scheduleId || id, name: staffName } : null,
        resource: scheduleId ? { _id: scheduleId, type: 'SCHEDULE' } : null,
      },
    },
  };
}

export async function getBookingsFromCache(start: Date, end: Date): Promise<BookingItem[]> {
  const q = query(
    collection(getDb(), 'bookings'),
    where('startDate', '>=', Timestamp.fromDate(start)),
    where('startDate', '<=', Timestamp.fromDate(end))
  );

  try {
    const t0 = performance.now();
    const [snap, amounts] = await Promise.all([
      getDocs(q),
      loadAmounts([where('startDate', '>=', Timestamp.fromDate(start)), where('startDate', '<=', Timestamp.fromDate(end))]),
    ]);
    console.log(`[bookingsCache] Firestore: ${snap.size} docs in ${(performance.now() - t0).toFixed(0)}ms`);
    return withAmounts(snap.docs.map(d => mapDoc(d.id, d.data())), amounts);
  } catch (err) {
    console.warn('[bookingsCache] getBookings failed:', err);
    return [];
  }
}

// A repeating block is cached as a MASTER doc (the pattern) plus one doc per
// INSTANCE (the real occurrences). The MASTER sits on the first occurrence's
// date, so it rendered as a duplicate there — and deleting only the first
// occurrence left it behind as a stand-in. Hide a MASTER whenever any of its
// INSTANCE/EXCEPTION docs are loaded; a MASTER with no instances is kept.
function dropMasterDuplicates(items: BookingItem[]): BookingItem[] {
  const seriesWithInstances = new Set(
    items
      .filter((b) => b.agendaItem?.recurrenceType === 'INSTANCE' || b.agendaItem?.recurrenceType === 'EXCEPTION')
      .map((b) => b.agendaItem?.recurringEventId)
      .filter((id): id is string => !!id)
  );
  return items.filter((b) => {
    if (b.agendaItem?.recurrenceType !== 'MASTER') return true;
    return !seriesWithInstances.has(b.agendaItem._id);
  });
}

export async function getBlockedSlotsFromCache(start: Date, end: Date): Promise<BookingItem[]> {
  const q = query(
    collection(getDb(), 'blockedSlots'),
    where('startDate', '>=', Timestamp.fromDate(start)),
    where('startDate', '<=', Timestamp.fromDate(end))
  );

  try {
    const t0 = performance.now();
    const snap = await getDocs(q);
    console.log(`[bookingsCache] Firestore blockedSlots: ${snap.size} docs in ${(performance.now() - t0).toFixed(0)}ms`);
    return dropMasterDuplicates(snap.docs.map(d => mapBlockedSlotDoc(d.id, d.data())));
  } catch (err) {
    console.warn('[bookingsCache] getBlockedSlots failed:', err);
    return [];
  }
}

/**
 * Fetch all bookings for a specific contact from Firestore cache.
 */
export async function getBookingsByContactFromCache(contactId: string): Promise<BookingItem[]> {
  const q = query(
    collection(getDb(), 'bookings'),
    where('contactId', '==', contactId)
  );
  try {
    const t0 = performance.now();
    const [snap, amounts] = await Promise.all([getDocs(q), loadAmounts([where('contactId', '==', contactId)])]);
    console.log(`[bookingsCache] contact ${contactId}: ${snap.size} docs in ${(performance.now() - t0).toFixed(0)}ms`);
    return withAmounts(snap.docs.map(d => mapDoc(d.id, d.data())), amounts);
  } catch (err) {
    console.warn('[bookingsCache] getBookingsByContact failed:', err);
    return [];
  }
}

/**
 * Fetch a single booking document by ID from Firestore cache.
 */
export async function getBookingByIdFromCache(bookingId: string): Promise<BookingItem | null> {
  try {
    const t0 = performance.now();
    const snap = await getDoc(doc(getDb(), 'bookings', bookingId));
    console.log(`[bookingsCache] booking ${bookingId}: ${snap.exists() ? 'hit' : 'miss'} in ${(performance.now() - t0).toFixed(0)}ms`);
    if (!snap.exists()) return null;
    const data = snap.data();
    // A doc can exist without being a real mirrored booking (a stub holding only
    // e.g. payment fields). mapDoc would turn that into an "Other" booking dated
    // right now with no staff/address, so callers never fall back to Wix. No
    // start date = not a usable booking = treat as a miss.
    if (!data?.startDate) {
      console.log(`[bookingsCache] booking ${bookingId}: incomplete doc (no startDate), treating as miss`);
      return null;
    }
    const booking = mapDoc(snap.id, data);
    if (await canReadAmounts()) {
      try {
        const amountSnap = await getDoc(doc(getDb(), 'bookingAmounts', bookingId));
        if (amountSnap.exists()) booking.price = priceFromAmounts(amountSnap.data());
      } catch (err) {
        console.warn('[bookingsCache] bookingAmount failed:', err);
      }
    }
    return booking;
  } catch (err) {
    console.warn('[bookingsCache] getBookingById failed:', err);
    return null;
  }
}

/**
 * Real-time subscription for upcoming bookings in a date range.
 * Fires immediately with cached data, then again with server data, and on any future changes.
 * Returns an unsubscribe function — call it on component unmount.
 */
export function subscribeToUpcomingBookings(
  start: Date,
  end: Date,
  onData: (bookings: BookingItem[]) => void,
  onError?: (err: Error) => void
): () => void {
  const q = query(
    collection(getDb(), 'bookings'),
    where('startDate', '>=', Timestamp.fromDate(start)),
    where('startDate', '<=', Timestamp.fromDate(end))
  );
  const t0 = performance.now();
  let n = 0;
  let latest: BookingItem[] | null = null;
  let amounts = new Map<string, number | undefined>();
  const emit = () => { if (latest) onData(withAmounts(latest, amounts)); };
  const unsubscribeBookings = onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snap) => {
      console.log(`[agenda-timing] bookings snapshot #${++n} +${Math.round(performance.now() - t0)}ms docs=${snap.size} fromCache=${snap.metadata.fromCache}`);
      latest = snap.docs.map(d => mapDoc(d.id, d.data()));
      emit();
    },
    (err) => onError?.(err)
  );

  // Owners and managers also follow the amounts for the same range and have them added to the bookings above
  let stopped = false;
  let unsubscribeAmounts: () => void = () => {};
  canReadAmounts().then((allowed) => {
    if (!allowed || stopped) return;
    unsubscribeAmounts = onSnapshot(
      query(
        collection(getDb(), 'bookingAmounts'),
        where('startDate', '>=', Timestamp.fromDate(start)),
        where('startDate', '<=', Timestamp.fromDate(end))
      ),
      (snap) => {
        amounts = new Map(snap.docs.map((d) => [d.id, priceFromAmounts(d.data())] as [string, number | undefined]));
        emit();
      },
      (err) => console.warn('[bookingsCache] bookingAmounts listener failed:', err)
    );
  });

  return () => {
    stopped = true;
    unsubscribeBookings();
    unsubscribeAmounts();
  };
}

/**
 * Starts the agenda's home-window listeners (today ±7 days, bookings + blocked
 * slots) at app launch, as soon as login is confirmed, so the connection, the
 * token refresh and the cache/server reads overlap with the splash/login
 * instead of starting when the agenda opens. The window bounds are computed
 * exactly like Calendar.tsx's, so the agenda's own listeners are identical
 * queries — the SDK shares one target between identical queries, so they
 * attach to data that has already arrived (or is mid-flight) rather than
 * starting a second read. Kept for the whole session by default so that
 * opening the agenda later (or returning to today after browsing another
 * week) also joins an already-synced, live target instead of starting a new
 * read; pass `holdMs` to release it sooner.
 */
export function prewarmAgendaWindow(holdMs?: number): () => void {
  const start = new Date();
  start.setDate(start.getDate() - 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setDate(end.getDate() + 7);
  end.setHours(23, 59, 59, 999);
  const unsubBookings = subscribeToUpcomingBookings(start, end, () => {});
  const unsubBlocked = subscribeToBlockedSlots(start, end, () => {});
  const release = () => { unsubBookings(); unsubBlocked(); };
  const timer = holdMs !== undefined ? setTimeout(release, holdMs) : undefined;
  return () => { if (timer !== undefined) clearTimeout(timer); release(); };
}

export function subscribeToBlockedSlots(
  start: Date,
  end: Date,
  onData: (bookings: BookingItem[]) => void,
  onError?: (err: Error) => void
): () => void {
  const q = query(
    collection(getDb(), 'blockedSlots'),
    where('startDate', '>=', Timestamp.fromDate(start)),
    where('startDate', '<=', Timestamp.fromDate(end))
  );

  const t0 = performance.now();
  let n = 0;
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snap) => {
      console.log(`[agenda-timing] blocked snapshot #${++n} +${Math.round(performance.now() - t0)}ms docs=${snap.size} fromCache=${snap.metadata.fromCache}`);
      onData(dropMasterDuplicates(snap.docs.map(d => mapBlockedSlotDoc(d.id, d.data()))));
    },
    (err) => onError?.(err)
  );
}

/**
 * Batch-fetch payment status for a list of booking IDs from Firestore (10 in parallel per chunk).
 * Returns an entry for every ID: Firestore data if present, otherwise { paid: false, paymentStatus: 'unknown' }.
 * Callers can use the returned set to know which IDs still need a Zoho API fallback (those with paymentStatus === 'unknown').
 */
export async function getPaymentStatusBatch(
  bookingIds: string[]
): Promise<Record<string, { paid: boolean; paymentStatus: string }>> {
  const db = getDb();
  const result: Record<string, { paid: boolean; paymentStatus: string }> = {};
  const CHUNK = 10;
  for (let i = 0; i < bookingIds.length; i += CHUNK) {
    const chunk = bookingIds.slice(i, i + CHUNK);
    const snaps = await Promise.all(chunk.map(id => getDoc(doc(db, 'bookings', id))));
    snaps.forEach((d, idx) => {
      const id = chunk[idx];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = d.exists() ? (d.data() as Record<string, any>) : null;
      if (data?.zohoPaid != null || data?.zohoPaymentStatus) {
        result[id] = {
          paid: Boolean(data.zohoPaid),
          paymentStatus: data.zohoPaymentStatus || (data.zohoPaid ? 'paid' : 'unpaid'),
        };
      } else {
        result[id] = { paid: false, paymentStatus: 'unknown' };
      }
    });
  }
  return result;
}
